import { HTTPClient, type ClientConfig } from './core/http-client.js';
import { DEFAULT_API_VERSION } from './core/constants.js';
import { MessengerConfigError } from './core/errors.js';
import { SendAPI } from './resources/send.js';
import { AttachmentsAPI } from './resources/attachments.js';
import { ModerationAPI } from './resources/moderation.js';
import { TemplatesAPI } from './resources/templates.js';
import { ProfileAPI } from './resources/profile.js';
import { ConversationsAPI } from './resources/conversations.js';

export interface MessengerConfig {
  accessToken?: string;
  version?: string;
  baseUrl?: string;
  timeout?: number;
  maxRetries?: number;
}

export class Messenger {
  public readonly send: SendAPI;
  public readonly attachments: AttachmentsAPI;
  public readonly moderation: ModerationAPI;
  public readonly templates: TemplatesAPI;
  public readonly profile: ProfileAPI;
  public readonly conversations: ConversationsAPI;

  private readonly httpClient: HTTPClient;

  constructor(config: MessengerConfig = {}) {
    this.validateConfig(config);

    const clientConfig: ClientConfig = {
      accessToken: config.accessToken,
      version: config.version || DEFAULT_API_VERSION,
      baseUrl: config.baseUrl,
      timeout: config.timeout,
      maxRetries: config.maxRetries,
    };

    this.httpClient = new HTTPClient(clientConfig);

    // Initialize API resources
    this.send = new SendAPI(this.httpClient);
    this.attachments = new AttachmentsAPI(this.httpClient);
    this.moderation = new ModerationAPI(this.httpClient);
    this.templates = new TemplatesAPI(this.httpClient);
    this.profile = new ProfileAPI(this.httpClient);
    this.conversations = new ConversationsAPI(this.httpClient);
  }

  private validateConfig(config: MessengerConfig): void {
    if (config.accessToken !== undefined) {
      if (typeof config.accessToken !== 'string' || config.accessToken.trim() === '') {
        throw new MessengerConfigError('Access token must be a non-empty string');
      }
    }

    if (config.version && typeof config.version !== 'string') {
      throw new MessengerConfigError('API version must be a string');
    }

    if (config.timeout && (typeof config.timeout !== 'number' || config.timeout <= 0)) {
      throw new MessengerConfigError('Timeout must be a positive number');
    }

    if (config.maxRetries && (typeof config.maxRetries !== 'number' || config.maxRetries < 0)) {
      throw new MessengerConfigError('Max retries must be a non-negative number');
    }
  }
}